import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ClipboardCheck, Download, Trash2, Eye,
  UserCheck, UserX, Wifi, WifiOff, BarChart3, TrendingDown, CalendarClock, BookOpen,
  CalendarDays, MoreHorizontal,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { useBranch } from "../../contexts/BranchContext";
import { usePlan } from "../../contexts/PlanContext";
import {
  PageHeader, AppButton, EmptyState, ConfirmModal,
  FilterButton, ActiveFilterChips, FilterPanel, FilterField, Input, FilterRow, FilterSelectField,
  useListFilters,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import HolidaysEventsModal from "../Common/HolidaysEventsModal";
import DataTable from "../Common/DataTable";
import { can } from "../../utils/access";
import "./attendance.scss";

/**
 * AttendanceManagementPage — Full attendance CRUD and reporting for admins.
 * Shows all sessions across batches with filtering, search, and export.
 *
 * Route: /attendance
 */
const AttendanceManagementPage = () => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranch();
  const { hasPlanFeature } = usePlan();
  const attendanceOn = hasPlanFeature("attendanceManagement");
  const canManage = can("attendance.manage");
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(0);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [deleteModal, setDeleteModal] = useState({ show: false, sessionId: null });
  const [exporting, setExporting] = useState(false);
  const [offlineEnabled, setOfflineEnabled] = useState(false);
  const [hevOpen, setHevOpen] = useState(false);
  const [hevEvents, setHevEvents] = useState([]);
  const [hevLoading, setHevLoading] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const optionsRef = useRef(null);

  // Filters — branch is driven by the global branch selector (top nav),
  // so there is no in-page Branch dropdown here.
  const [batches, setBatches] = useState([]);
  /* The FilterPanel pattern (D3), replacing an inline `d-none d-md-flex` strip
     of three controls PLUS a `d-md-none` button-and-modal carrying the same
     three. `empty: ""` throughout — every read below tests truthiness.
     `format` on the dates because a raw ISO string is not a chip label. */
  const filters = useListFilters(
    {
      batchId: {
        label: "Batch", empty: "",
        options: [{ value: "", label: "All Batches" }, ...batches.map((b) => ({ value: b._id, label: b.name }))],
      },
      startDate: { label: "From", empty: "" },
      endDate: { label: "To", empty: "" },
    },
    () => setPage(1),
  );
  const { batchId, startDate, endDate } = filters.values;

  // Fetch filter options and offline setting. Re-fetch batch options when the
  // global branch changes so the Batch dropdown stays scoped to that branch.
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [batchRes, offlineRes] = await Promise.all([
          apiClient.get("/batches/list?pageSize=200&fields=name"),
          apiClient.get("/attendance/check-offline-enabled").catch(() => ({ data: {} })),
        ]);
        setBatches(batchRes.data.batches || []);
        setOfflineEnabled(offlineRes.data.offlineAttendanceEnabled === true);
      } catch { /* ignore */ }
    };
    fetchOptions();
  }, [selectedBranchId]);

  // Close options dropdown on outside click.
  useEffect(() => {
    const close = (e) => {
      if (optionsRef.current && !optionsRef.current.contains(e.target)) setOptionsOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const openHev = async () => {
    setHevOpen(true);
    if (hevEvents.length === 0) {
      setHevLoading(true);
      try {
        const today = new Date().toISOString().split("T")[0];
        const future = new Date(Date.now() + 365 * 86400000).toISOString().split("T")[0];
        const { data } = await apiClient.get(`/institute/calendar?from=${today}&to=${future}`);
        setHevEvents(data.events || []);
      } catch { /* ignore */ }
      setHevLoading(false);
    }
  };

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: pageSize });
      if (batchId) params.set("batchId", batchId);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);

      const { data } = await apiClient.get(`/attendance/report?${params.toString()}`);
      setSessions(data.sessions || []);
      setTotal(data.total || 0);
    } catch {
      setToast({ show: true, message: "Failed to load attendance data.", type: "danger" });
    }
    setLoading(false);
    // selectedBranchId re-fetches on global branch switch (X-Branch-Id header).
    // Depends on the three VALUES rather than the filters object: that object is
    // rebuilt every render by the hook, so depending on it would make this
    // useCallback a new function each time and the effect below fire forever.
  }, [page, pageSize, batchId, startDate, endDate, selectedBranchId]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (batchId) params.set("batchId", batchId);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);

      const response = await apiClient.get(`/attendance/export?${params.toString()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `attendance_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setToast({ show: true, message: "Export failed.", type: "danger" });
    }
    setExporting(false);
  };

  const handleDelete = async () => {
    if (!deleteModal.sessionId) return;
    try {
      await apiClient.delete(`/attendance/session/${deleteModal.sessionId}`);
      setToast({ show: true, message: "Session deleted.", type: "success" });
      setDeleteModal({ show: false, sessionId: null });
      fetchSessions();
    } catch {
      setToast({ show: true, message: "Failed to delete session.", type: "danger" });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleTimeString("en-IN", {
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div className="attendance-page sl-page">
      <PageHeader
        icon={ClipboardCheck}
        title="Attendance Management"
        description="View, manage, and export attendance records across all batches"
      />

      {/* Filters + Options */}
      <div className="attendance-filters mb-3">
        {/* One filter UI on both viewports. This was a three-control inline strip
            plus a "Clear" button (desktop only) beside a mobile-only modal with
            the same three — and "Clear (n)" is not a chip row: it says how many
            filters are on, never WHICH, and cannot remove one (Phase 6.3). */}
        <div className="attendance-filters__actions">
          <FilterButton count={filters.count} onClick={filters.open} />

          {/* Options dropdown */}
          <div className="att-options" ref={optionsRef}>
            <AppButton
              variant="outline"
              size="sm"
              onClick={() => setOptionsOpen((o) => !o)}
              aria-haspopup="true"
              aria-expanded={optionsOpen}
            >
              <MoreHorizontal size={15} /> Options
            </AppButton>
            {optionsOpen && (
              <div className="att-options__menu" role="menu">
                <button type="button" className="att-options__item" role="menuitem"
                  onClick={() => { setOptionsOpen(false); openHev(); }}>
                  <CalendarDays size={14} /> Holidays &amp; Events
                </button>
                <div className="att-options__divider" />
                {attendanceOn && (
                <button type="button" className="att-options__item" role="menuitem"
                  onClick={() => { setOptionsOpen(false); navigate("/attendance/register"); }}>
                  <BookOpen size={14} /> Batch Register
                </button>
              )}
              {attendanceOn && (
                <button type="button" className="att-options__item" role="menuitem"
                  onClick={() => { setOptionsOpen(false); navigate("/attendance/absentees"); }}>
                  <UserX size={14} /> Daily Absentees
                </button>
              )}
              {attendanceOn && (
                <button type="button" className="att-options__item" role="menuitem"
                  onClick={() => { setOptionsOpen(false); navigate("/attendance/low-attendance"); }}>
                  <TrendingDown size={14} /> Low Attendance
                </button>
              )}
              {attendanceOn && (
                <button type="button" className="att-options__item" role="menuitem"
                  onClick={() => { setOptionsOpen(false); navigate("/attendance/leaves"); }}>
                  <CalendarClock size={14} /> Leave Requests
                </button>
              )}
              <button type="button" className="att-options__item" role="menuitem"
                onClick={() => { setOptionsOpen(false); navigate("/attendance/analytics"); }}>
                <BarChart3 size={14} /> Analytics
              </button>
              {offlineEnabled && canManage && (
                <button type="button" className="att-options__item" role="menuitem"
                  onClick={() => { setOptionsOpen(false); navigate("/attendance/offline"); }}>
                  <WifiOff size={14} /> Mark Offline
                </button>
              )}
              <div className="att-options__divider" />
              <button type="button" className="att-options__item" role="menuitem"
                disabled={exporting}
                onClick={() => { setOptionsOpen(false); handleExport(); }}>
                <Download size={14} /> {exporting ? "Exporting…" : "Export CSV"}
              </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ActiveFilterChips items={filters.activeFilters} onClearAll={filters.reset} />

      {/* Sessions Table.
          `loading` is handed to DataTable rather than gating it: DataTable has
          rendered a real <tr>/<td> Shimmer skeleton since Phase 6.5, which keeps
          the column geometry so the page does not reflow — and a spinner in front
          of it meant 23 pages had that skeleton and this one did not. */}
      {!loading && sessions.length === 0 ? (
        <EmptyState
          icon={ClipboardCheck}
          title="No attendance records"
          description="Attendance records will appear here once classes are conducted."
        />
      ) : (
        <>
          <DataTable
            data={sessions}
            loading={loading}
            columns={[
              {
                key: "date",
                label: "Date",
                render: (s) => (
                  <div className="attendance-date-cell">
                    <span className="attendance-date-cell__date">{formatDate(s.sessionDate)}</span>
                    <span className="attendance-date-cell__time">
                      {formatTime(s.sessionStart)} – {formatTime(s.sessionEnd)}
                    </span>
                  </div>
                ),
              },
              { key: "batch", label: "Batch", render: (s) => s.batchName },
              { key: "branch", label: "Branch", render: (s) => s.branchName || "-" },
              { key: "instructor", label: "Instructor", render: (s) => s.instructorName || "-" },
              { key: "duration", label: "Duration", render: (s) => `${s.durationMins} min` },
              { key: "online", label: (<><Wifi size={14} /> Online</>), render: (s) => s.online },
              { key: "offline", label: (<><WifiOff size={14} /> Offline</>), render: (s) => s.manual },
              { key: "present", label: (<><UserCheck size={14} /> Present</>), className: "attendance-cell--present", render: (s) => s.present },
              { key: "absent", label: (<><UserX size={14} /> Absent</>), className: "attendance-cell--absent", render: (s) => s.absent + s.dropped },
              {
                key: "manual",
                label: "Manual",
                render: (s) =>
                  s.manualAttendanceCompleted ? (
                    <span className="attendance-badge attendance-badge--done">Done</span>
                  ) : (
                    <span className="attendance-badge attendance-badge--pending">Pending</span>
                  ),
              },
              {
                key: "actions",
                label: "Actions",
                render: (s) => (
                  <div className="attendance-actions">
                    <button
                      className="attendance-action-btn"
                      title="View Details"
                      onClick={() => navigate(`/attendance/session/${s._id}`)}
                    >
                      <Eye size={14} />
                    </button>
                    {canManage && (
                      <button
                        className="attendance-action-btn attendance-action-btn--danger"
                        title="Delete Session"
                        onClick={() => setDeleteModal({ show: true, sessionId: s._id })}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ),
              },
            ]}
            pagination={{
              page,
              pageSize,
              total,
              onPageChange: setPage,
              onPageSizeChange: (size) => { setPageSize(size); setPage(1); },
              pageSizeOptions: [15, 30, 60, 120],
            }}
          />
        </>
      )}

      <FilterPanel {...filters.panelProps} title="Filter Attendance">
        <FilterSelectField {...filters.selectProps("batchId")} />
        <FilterRow>
          <FilterField label="From">
            <Input size="lg" type="date" value={startDate} onChange={(e) => filters.set("startDate", e.target.value)} />
          </FilterField>
          <FilterField label="To">
            <Input size="lg" type="date" value={endDate} onChange={(e) => filters.set("endDate", e.target.value)} />
          </FilterField>
        </FilterRow>
      </FilterPanel>

      <HolidaysEventsModal
        show={hevOpen}
        onHide={() => setHevOpen(false)}
        events={hevEvents}
        loading={hevLoading}
      />

      {/* Delete Confirmation */}
      <ConfirmModal
        show={deleteModal.show}
        onHide={() => setDeleteModal({ show: false, sessionId: null })}
        onConfirm={handleDelete}
        title="Delete Attendance Session?"
        message="This will permanently delete this attendance session and all its records. This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default AttendanceManagementPage;
